import { ReactElement, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { duplicateVenueCategory } from 'src/api/duplicateVenueCategory';
import { updateVenueCategory } from 'src/api/updateVenueCategory';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { useVenueCategories } from 'src/hooks/useVenueCategories';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import { getBrandId } from 'src/helpers/getBrandId/getBrandId';
import { DuplicateNameTrayContent } from '../DuplicateNameTray';

const MAX_NAME_LENGTH = 255;
const TOP_DISPLAY_ORDER = 1;

export interface DuplicateVenueCategoryTrayContentProps {
  categoryId: string;
  sourceName?: string;
  onClose: () => void;
  openId: number;
}

export const DuplicateVenueCategoryTrayContent = ({
  categoryId,
  sourceName,
  onClose,
  openId,
}: DuplicateVenueCategoryTrayContentProps): ReactElement => {
  const queryClient = useQueryClient();
  const { trackEvent, captureException } = useUserTracking();
  const { data: venueCategoriesData } = useVenueCategories();
  const existingCategoryNames = useMemo(
    () =>
      venueCategoriesData?.categories
        ?.filter((category) => category.category_id !== null)
        .map((category) => category.name) ?? [],
    [venueCategoriesData],
  );

  const handleDuplicate = async (name: string) => {
    const { category_id: newCategoryId } = await duplicateVenueCategory(categoryId, { name });

    try {
      await updateVenueCategory({ categoryId: newCategoryId, newDisplayOrder: TOP_DISPLAY_ORDER });
    } catch (error) {
      captureException(error, { tags: { action: 'venue_category_duplicate_reorder' } });
    }

    await queryClient.invalidateQueries({ queryKey: [getBrandId(), QueryKeys.venueCategories] });
    trackEvent('venue_category_duplicated', { categoryId });
  };

  return (
    <DuplicateNameTrayContent
      entityLabel="category"
      nameLabel="Category Name"
      sourceName={sourceName}
      existingNames={existingCategoryNames}
      maxLength={MAX_NAME_LENGTH}
      errorTag="venue_category_duplicate"
      openId={openId}
      onDuplicate={handleDuplicate}
      onClose={onClose}
    />
  );
};

export default DuplicateVenueCategoryTrayContent;
